import React, { useState, useEffect, useCallback } from 'react';
import { resellersAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Pagination from '../../components/common/Pagination';
import PermissionGuard from '../../components/common/PermissionGuard';
import Spinner from '../../components/common/Spinner';
import toast from 'react-hot-toast';

const EMPTY_FORM = { name: '', email: '', phone: '', company: '', notes: '' };

const Resellers = () => {
  const [resellers, setResellers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [pagination, setPagination] = useState(null);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [showModal, setShowModal]   = useState(false);
  const [editReseller, setEditReseller] = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);

  const fetchResellers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await resellersAPI.getAll({ page, limit: 15, search });
      setResellers(res.data.data);
      setPagination(res.data.pagination);
    } catch { toast.error('Failed to load resellers'); }
    finally { setLoading(false); }
  }, [page, search]);

  useEffect(() => { fetchResellers(); }, [fetchResellers]);

  const openCreate = () => { setEditReseller(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (r) => {
    setEditReseller(r);
    setForm({ name: r.name, email: r.email, phone: r.phone || '', company: r.company || '', notes: r.notes || '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editReseller) {
        await resellersAPI.update(editReseller._id, form);
        toast.success('Reseller updated');
      } else {
        await resellersAPI.create(form);
        toast.success('Reseller created');
      }
      setShowModal(false);
      fetchResellers();
    } catch (err) { toast.error(err.response?.data?.message || 'Operation failed'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (id) => {
    try {
      await resellersAPI.toggleStatus(id);
      toast.success('Status updated');
      fetchResellers();
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reseller?')) return;
    try {
      await resellersAPI.delete(id);
      toast.success('Reseller deleted');
      fetchResellers();
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{pagination?.total || 0} total resellers</p>
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Search resellers..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <PermissionGuard module="resellers" action="create">
            <button
              onClick={openCreate}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition"
            >
              + Add Reseller
            </button>
          </PermissionGuard>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12"><Spinner /></div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-6 py-3 font-medium">Reseller</th>
                  <th className="px-6 py-3 font-medium">Company</th>
                  <th className="px-6 py-3 font-medium">Phone</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Created</th>
                  <th className="px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {resellers.map(r => (
                  <tr key={r._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-medium text-sm">
                          {r.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{r.name}</p>
                          <p className="text-gray-500 text-xs">{r.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-700">{r.company || <span className="text-gray-300">—</span>}</td>
                    <td className="px-6 py-4 text-gray-700">{r.phone || <span className="text-gray-300">—</span>}</td>
                    <td className="px-6 py-4">
                      <Badge status={r.isActive ? 'active' : 'inactive'} />
                    </td>
                    <td className="px-6 py-4 text-gray-500 text-xs">{formatDate(r.createdAt)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <PermissionGuard module="resellers" action="update">
                          <button onClick={() => openEdit(r)} className="text-indigo-600 hover:underline text-xs">Edit</button>
                          <button onClick={() => handleToggle(r._id)} className="text-yellow-600 hover:underline text-xs">
                            {r.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </PermissionGuard>
                        <PermissionGuard module="resellers" action="delete">
                          <button onClick={() => handleDelete(r._id)} className="text-red-600 hover:underline text-xs">Delete</button>
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))}
                {resellers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                      <p className="text-3xl mb-2">🤝</p>
                      <p>No resellers found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <Pagination pagination={pagination} onPageChange={setPage} />
          </>
        )}
      </div>

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editReseller ? 'Edit Reseller' : 'Add Reseller'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
              <input
                type="text" required
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
              <input
                type="email" required
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
              <input
                type="text"
                value={form.company}
                onChange={e => setForm({ ...form, company: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="text"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm resize-none"
              placeholder="Optional notes about this reseller..."
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button" onClick={() => setShowModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={saving}
              className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
            >
              {saving ? 'Saving...' : (editReseller ? 'Update' : 'Add Reseller')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Resellers;
